import React, { useEffect, useState } from 'react';
import SimpleCard from '../../../utils/card/SimpleCard';
import { Card, CardBody, Col, Container, Label, Media, Row, Spinner } from 'reactstrap';
import { Box, IconButton } from '@mui/material';
import ArrowBack from '@material-ui/icons/ArrowBack';
import MaterialReactTable from 'material-react-table';
import moment from 'moment';
import { getRegs } from '../../../utils/fc';
import Select from '../../../components/Template/Input/Select';
import TimelineStatusProdutoSmall from '../../../components/TimelineStatusProdutoSmall/TimelineStatusProdutoSmall';
import ReactApexChart from 'react-apexcharts';
import { monthRefDefault } from '../../../utils/monthRef';
import { buildMonthSelectOptions, mmYyyyFromMesRef, mesRefFromMmYyyy } from '../../../utils/monthSelectOptions';
import {
  PROD_DASHBOARD_FIELDS,
  buildAllMarcosOverview,
  buildDashboardSlices,
  getMarcoLimiteDate,
  hasResponsavel,
  MARCO_DISPLAY_ORDER,
  monthToTextFromRef,
  statusCounts,
} from './dashboardProdutosHelpers';
import MarcoOverviewChart from './MarcoOverviewChart';

function processProducts(products) {
  const seenRecIds = {};
  const result = [];
  const renamed = [];
  const pacotes = {};

  for (const product of products) {
    const { rec_id, prod_desc, prod_id } = product;

    if (!seenRecIds[rec_id] || rec_id === null) {
      // First time seeing this rec_id
      seenRecIds[rec_id] = { count: 0, originalName: prod_desc };
      result.push(product);
    } else {
      // Duplicate rec_id found, increment counter and rename
      seenRecIds[rec_id].count += 1;
      const count = seenRecIds[rec_id].count;
      const index = result.findIndex(p => p.rec_id === rec_id)
      result[index] = {
        ...result[index],
        prod_desc: `${seenRecIds[rec_id].originalName} +${count}`,
        pacote: true
      };
      if (!pacotes[rec_id]) {
        const primeiroProdutoDoPacote = products.find(p => p.prod_id === result.filter(p => p.rec_id === rec_id)[0].prod_id);
        pacotes[rec_id] = [product, primeiroProdutoDoPacote];
      } else {
        pacotes[rec_id] = [...pacotes[rec_id], product];
      }
    }
  }

  return { filteredList: result, renamedDuplicates: renamed, pacotes: pacotes };
}

const DashboardProdutos = ({ produtos }) => {
  const [mesRef, setMesRef] = useState(monthRefDefault());
  const [rawProducts, setRawProducts] = useState([]);
  const [data, setData] = useState([]);
  const [pacotes, setPacotes] = useState({});
  const [dataIsLoading, setDataIsLoading] = useState(true);
  const [showDetails, setShowDetails] = useState(false);
  const [productsInTheTable, setProductsInTheTable] = useState([]);
  const [productDetails, setProductDetails] = useState({});
  const [showBackButton, setShowBackButton] = useState(false);
  const [title, setTitle] = useState('');
  const [overviewFilter, setOverviewFilter] = useState(null);

  const overview = React.useMemo(
    () => (rawProducts.length ? buildAllMarcosOverview(rawProducts, mesRef) : null),
    [rawProducts, mesRef],
  );

  const slices = React.useMemo(
    () => (data.length ? buildDashboardSlices(data, mesRef) : null),
    [data, mesRef],
  );

  const {
    produtosMes = [],
    produtosTri = [],
    produtos60Dias = [],
    produtos30Dias = [],
    produtosMesLista = [],
  } = slices || {};

  const defaultListTitle = `Produtos de ${monthToTextFromRef(mesRef)}`;

  useEffect(() => {
    if (!title) setTitle(defaultListTitle);
  }, [defaultListTitle, title]);

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setDataIsLoading(true);
      try {
        const resData = await getRegs('prod', { prod_ativo: 1 }, PROD_DASHBOARD_FIELDS);
        if (cancelled) return;
        const withResponsavel = resData.filter(hasResponsavel);
        setRawProducts(withResponsavel);
        const processedProducts = processProducts(
          withResponsavel.filter((p) => p.prod_dtLimAprov),
        );
        setData(processedProducts.filteredList);
        setPacotes(processedProducts.pacotes);
      } finally {
        if (!cancelled) setDataIsLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!slices) return;
    if (!showBackButton && !overviewFilter) {
      setProductsInTheTable(slices.produtosMesLista);
      setTitle(defaultListTitle);
    }
  }, [mesRef, data, showBackButton, overviewFilter, defaultListTitle, slices]);

  const handleOverviewBarClick = (marcoId, bucket) => {
    if (!overview) return;
    const isSame = overviewFilter?.marcoId === marcoId && overviewFilter?.bucket === bucket;
    if (isSame) {
      setOverviewFilter(null);
      setShowBackButton(false);
      setTitle(defaultListTitle);
      if (slices) setProductsInTheTable(slices.produtosMesLista);
      return;
    }
    const { marco, productsByBucket } = overview[marcoId];
    const list = [...(productsByBucket[bucket] || [])].sort((a, b) => {
      const da = getMarcoLimiteDate(a, marco)?.getTime() ?? 0;
      const db = getMarcoLimiteDate(b, marco)?.getTime() ?? 0;
      return da - db;
    });
    setOverviewFilter({ marcoId, bucket });
    setProductsInTheTable(list);
    setShowBackButton(true);
    setTitle(`${marco.title} — ${marco.labels[bucket]} (${monthToTextFromRef(mesRef)})`);
  };

  if (dataIsLoading) {
    return (
      <div className="centered">
        <Spinner />
      </div>
    );
  }

  return (
    <Container fluid>
      <Row className="mb-3">
        <Col xl={3} lg={3} md={4} sm={6}>
          <Label>Filtrar por Mês</Label>
          <Select
            isClearable={false}
            isSearchable={false}
            placeholder="Mês"
            value={{
              value: mmYyyyFromMesRef(mesRef),
              label: mmYyyyFromMesRef(mesRef),
            }}
            options={buildMonthSelectOptions(12).map((opt) => ({
              value: opt.value,
              label: opt.label,
            }))}
            onChange={(monthFilter) => {
              const ref = mesRefFromMmYyyy(monthFilter.value);
              setMesRef(ref);
              setOverviewFilter(null);
              setShowBackButton(false);
              setTitle(`Produtos de ${monthToTextFromRef(ref)}`);
            }}
          />
        </Col>
      </Row>

      <h4 className="font-size-16 mb-3" style={{ fontWeight: 700 }}>Visão geral</h4>
      <Row className="mb-4">
        {overview && MARCO_DISPLAY_ORDER.map((marcoId) => (
          <Col key={marcoId} xl={4} lg={4} md={12} className="mb-3 mb-xl-0">
            <MarcoOverviewChart
              overview={overview[marcoId]}
              activeFilter={overviewFilter}
              onBarClick={handleOverviewBarClick}
            />
          </Col>
        ))}
      </Row>

      <hr className="mb-4" />
      <h4 className="font-size-16 mb-3" style={{ fontWeight: 700 }}>Operacional</h4>

      <div className="d-flex justify-content-between align-items-center">
        <CardProdutosMes
          mesRef={mesRef}
          pacotes={pacotes}
          onClick={() => {
            setOverviewFilter(null);
            setProductsInTheTable(produtosMes);
            setShowBackButton(true);
            setTitle(`Produtos de ${monthToTextFromRef(mesRef)}`);
          }}
          produtosMes={produtosMes}
          className="mr-4"
        />
        <CardProdutosTri
          mesRef={mesRef}
          pacotes={pacotes}
          onClick={() => {
            setOverviewFilter(null);
            setProductsInTheTable(produtosTri);
            setShowBackButton(true);
            setTitle('Produtos do trimestre');
          }}
          produtosTri={produtosTri}
          className="mr-4"
        />
        <CardProdutosProximos60
          pacotes={pacotes}
          onClick={() => {
            setOverviewFilter(null);
            setProductsInTheTable(produtos60Dias);
            setShowBackButton(true);
            setTitle('Produtos próximos 60 dias');
          }}
          produtos60Dias={produtos60Dias}
          className="mr-4"
        />
        <CardProdutosProximos30
          pacotes={pacotes}
          onClick={() => {
            setOverviewFilter(null);
            setProductsInTheTable(produtos30Dias);
            setShowBackButton(true);
            setTitle('Produtos próximos 30 dias');
          }}
          produtos30Dias={produtos30Dias}
        />
      </div>
      <div className="d-flex">
        <div className="d-flex flex-column" style={{ width: '50%' }}>
          <CardProdutosTop10Mes
            pacotes={pacotes}
            setProductDetails={setProductDetails}
            setShowDetails={setShowDetails}
            productsInTheTable={productsInTheTable}
            produtosMesLista={produtosMesLista}
            showBackButton={showBackButton}
            title={title}
            mesRef={mesRef}
            overviewFilter={overviewFilter}
            setOverviewFilter={setOverviewFilter}
            setShowBackButton={setShowBackButton}
            setTitle={setTitle}
            setProductsInTheTable={setProductsInTheTable}
          />
        </div>
        <div className="d-flex flex-column ml-2 mr-4" style={{ width: '50%' }}>
          {showDetails && <CardProdutoDetalhes productDetails={productDetails} />}
        </div>
      </div>
      <CardGraficoCoordenadores pacotes={pacotes} produtos={produtosMes} mesRef={mesRef} />
    </Container>
  );
};

const StatusBreakdown = ({ list, pacotes }) => {
  const c = statusCounts(list, pacotes);
  const rows = [
    { n: c.naoEntregues, label: 'não entregues' },
    { n: c.aguardando, label: 'aguardando aprovação' },
    { n: c.reprovados, label: 'reprovados' },
    { n: c.aprovados, label: 'aprovados' },
  ];
  return (
    <div style={{ width: '100%' }} className="d-flex flex-column">
      {rows.map((row) => (
        <div key={row.label} style={{ display: 'flex', width: '100%' }}>
          <div style={{ display: 'flex', width: '50px' }}>
            <span style={{ fontWeight: 'bold', fontSize: '14px' }}>{row.n}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-start', width: '100%' }}>
            <span style={{ fontSize: '14px' }}> {row.label}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

const CardProdutosMes = ({ mesRef, produtosMes, pacotes, onClick, ...props }) => {
  const [hovered, setHovered] = useState(false);
  const c = statusCounts(produtosMes, pacotes);
  return (
    <div {...props} style={{ width: '100%' }} onClick={onClick}>
      <Card
        style={{
          transition: 'border 0.3s ease',
          border: hovered ? '2px solid #505A62' : '2px solid transparent',
          cursor: 'pointer',
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <CardBody>
          <Media>
            <Media body className="overflow-hidden">
              <div className="d-flex justify-content-between align-items-center">
                <span style={{ fontWeight: 'bold' }} className="text-truncate font-size-14 mb-2">
                  {`Produtos de ${monthToTextFromRef(mesRef)}`}
                </span>
                <span style={{ fontWeight: 'bold', fontSize: '26px' }} className="text-truncate mb-2">
                  {c.total}
                </span>
              </div>
              <StatusBreakdown list={produtosMes} pacotes={pacotes} />
            </Media>
          </Media>
        </CardBody>
      </Card>
    </div>
  );
};

const CardProdutosTri = ({ pacotes, onClick, produtosTri, ...props }) => {
  const [hovered, setHovered] = useState(false);
  const c = statusCounts(produtosTri, pacotes);
  return (
    <div {...props} style={ { width: '100%' } } onClick={onClick}>
      <Card
        style={{
          transition: 'border 0.3s ease',
          border: hovered ? '2px solid #505A62' : '2px solid transparent',
          cursor: 'pointer',
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <CardBody>
          <Media>
            <Media body className="overflow-hidden">
              <div className="d-flex justify-content-between align-items-center">
                <span style={ { fontWeight: 'bold' } } className="text-truncate font-size-14 mb-2">
                  Produtos do trimestre
                </span>
                <span style={ { fontWeight: 'bold', fontSize: '26px' } } className="text-truncate mb-2">
                  {c.total}
                </span>
              </div>
              <StatusBreakdown list={produtosTri} pacotes={pacotes} />
            </Media>
          </Media>
        </CardBody>
      </Card>
    </div>
  );
};

const CardProdutosProximos60 = ({ pacotes, onClick, produtos60Dias, ...props }) => {
  const [hovered, setHovered] = useState(false);
  const c = statusCounts(produtos60Dias, pacotes);
  return (
    <div {...props} style={ { width: '100%' } } onClick={onClick}>
      <Card
        style={{
          transition: 'border 0.3s ease',
          border: hovered ? '2px solid #505A62' : '2px solid transparent',
          cursor: 'pointer',
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <CardBody>
          <Media>
            <Media body className="overflow-hidden">
              <div className="d-flex justify-content-between align-items-center">
                <span style={ { fontWeight: 'bold' } } className="text-truncate font-size-14 mb-2">
                  Produtos próximos 60 dias
                </span>
                <span style={ { fontWeight: 'bold', fontSize: '26px' } } className="text-truncate mb-2">
                  {c.total}
                </span>
              </div>
              <StatusBreakdown list={produtos60Dias} pacotes={pacotes} />
            </Media>
          </Media>
        </CardBody>
      </Card>
    </div>
  );
};

const CardProdutosProximos30 = ({ pacotes, onClick, produtos30Dias, ...props }) => {
  const [hovered, setHovered] = useState(false);
  const c = statusCounts(produtos30Dias, pacotes);
  return (
    <div {...props} style={ { width: '100%' } } onClick={onClick}>
      <Card
        style={{
          transition: 'border 0.3s ease',
          border: hovered ? '2px solid #505A62' : '2px solid transparent',
          cursor: 'pointer',
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <CardBody>
          <Media>
            <Media body className="overflow-hidden">
              <div className="d-flex justify-content-between align-items-center">
                <span style={ { fontWeight: 'bold' } } className="text-truncate font-size-14 mb-2">
                  Produtos próximos 30 dias
                </span>
                <span style={ { fontWeight: 'bold', fontSize: '26px' } } className="text-truncate mb-2">
                  {c.total}
                </span>
              </div>
              <StatusBreakdown list={produtos30Dias} pacotes={pacotes} />
            </Media>
          </Media>
        </CardBody>
      </Card>
    </div>
  );
};

const CardProdutoDetalhes = ({ ...props }) => {
  return (
    <div {...props} className='ml-4' style={{ width: '100%' }}>
      <SimpleCard
        standard={false}
        title={<h4 className="text-truncate font-size-14 mb-2">{`Detalhes do produto ${props.productDetails.prod_desc}`}</h4>}
        text={<TextCardDetalhesProduto productDetails={props.productDetails} />}
      />
    </div>
  );
};

const CardProdutosTop10Mes = ({ ...props }) => {
  return (
    <div>
      <SimpleCard
        standard={false}
        title={
          <div className='d-flex justify-content-between align-items-center'>
            <h4 className="text-truncate font-size-14 mb-2">
              {props.title}
            </h4>
            {props.showBackButton &&
              <IconButton onClick={() => {
                props.setOverviewFilter(null);
                props.setProductsInTheTable(props.produtosMesLista);
                props.setShowBackButton(false);
                props.setTitle(`Produtos de ${monthToTextFromRef(props.mesRef)}`);
              }}>
                <ArrowBack />
              </IconButton>
            }
          </div>
        }
        text={<TableTop10Produtos
          pacotes={props.pacotes}
          setProductDetails={props.setProductDetails}
          setShowDetails={props.setShowDetails}
          productsInTheTable={props.productsInTheTable}
        />}
        footer={''}
        additionalData={props.additionalData}
      />
    </div>
  );
};

const TextCardDetalhesProduto = ({ ...props }) => {
  return (
    <div className='d-flex flex-column justify-content-between'>
      <Box sx={{ padding: '16px' }}>
        <Row>
          <Col>
            <Row className="align-items-center">
              <Col>
                <Row className="mt-2 align-items-center">
                  <Col>
                    <TimelineStatusProdutoSmall
                      produto={props.productDetails}
                      actual={props.productDetails.prodStatusActual_id || 0}
                      tecResp={props.productDetails.prod_responsavelNome ?? props.productDetails.prod_responsavelNomeAtual}
                      dataContratual={moment(props.productDetails.prod_dtLimAprov).format('DD/MM/YYYY')}
                      dataEntregueAoEp={moment(props.productDetails.prodStatus3_data).format('DD/MM/YYYY')}
                      dataEntregueAGestao={moment(props.productDetails.prodStatus4_data).format('DD/MM/YYYY')}
                      prod_aprovCompliance={props.productDetails.prod_aprovCompliance}
                      prodStatusAlt_obs={props.productDetails.prodStatusAlt_obs}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>
        </Row>
      </Box>
      <div className='d-flex justify-content-between'>
        <div className='d-flex flex-column'>
          <div className='mb-2'>
            Data limite para faturamento:{' '}
            <span style={{ fontWeight: 'bold' }}>
              {props.productDetails.prod_dtLimAprov
                ? moment(props.productDetails.prod_dtLimAprov).format('DD/MM/YYYY')
                : 'Sem data limite para faturamento'}
            </span>
          </div>
          {props.productDetails.prod_dtEntrega && (
            <div className='mb-2'>
              Data limite entrega:{' '}
              <span style={{ fontWeight: 'bold' }}>
                {moment(props.productDetails.prod_dtEntrega).format('DD/MM/YYYY')}
              </span>
              {props.productDetails.prodEntregueAoClienteData && (
                <span className="text-muted" style={{ fontSize: '12px' }}>
                  {' '}(entregue em {moment(props.productDetails.prodEntregueAoClienteData).format('DD/MM/YYYY')})
                </span>
              )}
            </div>
          )}
          {props.productDetails.prod_dtAprov && (
            <div className='mb-2'>
              Data limite aprovação:{' '}
              <span style={{ fontWeight: 'bold' }}>
                {moment(props.productDetails.prod_dtAprov).format('DD/MM/YYYY')}
              </span>
              {props.productDetails.prodAprovacaoData && (
                <span className="text-muted" style={{ fontSize: '12px' }}>
                  {' '}(aprovado em {moment(props.productDetails.prodAprovacaoData).format('DD/MM/YYYY')})
                </span>
              )}
            </div>
          )}
          {props.productDetails.prodAutFatData && (
            <div className='mb-2'>
              Faturamento autorizado em:{' '}
              <span style={{ fontWeight: 'bold' }}>
                {moment(props.productDetails.prodAutFatData).format('DD/MM/YYYY')}
              </span>
            </div>
          )}
          <div className='mb-2'>
            Status:{' '}
            <span style={{ fontWeight: 'bold' }}>{props.productDetails.prodStatusActual_nome || '—'}</span>
          </div>
          <div className='mb-2'>
            Responsável:{' '}
            <span style={{ fontWeight: 'bold' }}>
              {props.productDetails.prod_responsavelNome ?? props.productDetails.prod_responsavelNomeAtual ?? 'Sem responsável'}
            </span>
          </div>
          <div>
            Projeto: <span style={{ fontWeight: 'bold' }}>{props.productDetails.proj_nome}</span>
          </div>
        </div>
        <div className='d-flex flex-column justify-content-end align-items-end'>
          <a target="_blank" rel="noreferrer" href={`https://oikos.ecoss.is/oikos-client/dep-tecnico/projetos/${props.productDetails.proj_id}`}>Projeto</a>
          {props.productDetails.prod_drive && (
            <a target="_blank" rel="noreferrer" href={props.productDetails.prod_drive}>Pasta do produto</a>
          )}
        </div>
      </div>
    </div>
  );
};


const columns = [
  {
    accessorKey: 'prod_desc',
    header: 'Descrição',
    size: 200,
  },
  {
    accessorKey: 'proj_nome',
    header: 'Projeto',
    accessorFn: (row) => row.proj_nome || '—',
    size: 200,
  },
  {
    accessorKey: 'prod_dtLimAprov',
    header: 'Data limite faturamento',
    accessorFn: (row) => row.prod_dtLimAprov,
    sortingFn: 'datetime',
    Cell: ({ cell, row }) => {
      if (row.original.prod_dtLimAprov) {
        return moment(row.original.prod_dtLimAprov).format('DD/MM/YYYY')
      } else {
        return 'Sem data limite para faturamento'
      }
    },
    size: 100,
  },
]

const TableTop10Produtos = ({ ...props }) => {
  const handleRowClick = (rowOriginal) => {
    props.setShowDetails(true);
    props.setProductDetails(rowOriginal);
  };

  return (
    <MaterialReactTable
      columns={columns}
      data={props.productsInTheTable}
      initialState={{
        density: 'compact',
        pagination: { pageIndex: 0, pageSize: 10 },
        sorting: [{ id: 'prod_dtLimAprov', desc: false }],
      }}
      enableColumnResizing
      enableGrouping={false}
      enableGlobalFilter
      enableFilters={false}
      enableRowExpansion={false}
      enableExpanding={false}
      enablePagination={props.productsInTheTable.length > 10}
      muiTopToolbarProps={{
        sx: {
          '.MuiBox-root': { marginTop: '10px', height: '20px', display: 'flex', alignItems: 'center' },
          '.MuiSvgIcon-root': { width: '12px' },
          '.MuiButtonBase-root': { padding: '5px' },
        },
      }}
      muiTableBodyCellProps={{ sx: { fontSize: '10px' } }}
      muiTablePaginationProps={{
        sx: { '.MuiTablePagination-displayedRows': { fontSize: '12px' }, marginTop: '0px', fontSize: '12px' },
        labelRowsPerPage: <span style={{ fontSize: '12px' }}>Produtos por página</span>,
        rowsPerPageOptions: [10, 50, 100],
      }}
      positionPagination="bottom"
      muiTableHeadCellColumnActionsButtonProps={{ size: '8px' }}
      enableDensityToggle={false}
      enableColumnFilters={false}
      muiTableHeadCellProps={{
        sx: {
          '.MuiSvgIcon-root': { fontSize: '18px' },
          fontSize: '12px',
          fontWeight: 'normal',
          padding: '8px',
        },
      }}
      muiTableBodyRowProps={({ row }) => ({
        onClick: () => handleRowClick(row.original),
        sx: { cursor: 'pointer' },
      })}
    />
  );
};

const CardGraficoCoordenadores = ({...props }) => {
  return (
    <div className='pl-2' style={{width: "100%"}}>
      <SimpleCard
        standard={false}
        title={``}
        text={<TextCardGraficoCoordenadores pacotes={props.pacotes} produtos={props.produtos} mesRef={props.mesRef} />}
        footer={''}
      />
    </div>
  )
}

const TextCardGraficoCoordenadores = ({...props}) => {

  const stockMap = {};

  props.produtos.filter(p => (p.prod_responsavelNome || p.prod_responsavelNomeAtual) && !p.pacote).forEach(item => {
    const resp = item.prod_responsavelNome || item.prod_responsavelNomeAtual;
    if (!stockMap[resp]) {
      stockMap[resp] = {
        coordenador: resp,
        aprovados: 0,
        atrasados: 0,
        total: 0,
      };
    }
    if (item.prodStatusActual_id === 7) {
      stockMap[resp].aprovados++;
    } else if (item.prodStatusActual_id < 7 && new Date(item.prod_dtLimAprov) < new Date()) {
      stockMap[resp].atrasados++;
    }
    stockMap[resp].total++;
  });

  Object.values(props.pacotes).flat().forEach(item => {
    const resp = item.prod_responsavelNome ?? item.prod_responsavelNomeAtual;
    if (!stockMap[resp]) {
      stockMap[resp] = {
        coordenador: resp,
        aprovados: 0,
        atrasados: 0,
        total: 0,
      };
    }
    if (item.prodStatusActual_id === 7) {
      stockMap[resp].aprovados++;
    } else if (item.prodStatusActual_id < 7 && new Date(item.prod_dtLimAprov) < new Date()) {
      stockMap[resp].atrasados++;
    }
    stockMap[resp].total++;
  })

  // Step 2: Convert the map to a list
  const result = Object.values(stockMap);

  const serieAtrasados = result
    .map(item => item.atrasados)
  const serieAprovados = result
    .map(item => item.aprovados)
  const serieTotal = result
    .map(item => item.total)
  const serieLabel = result
    .map(item => item.coordenador)

  const options = {
    labels: serieLabel,
    chart: {
      type: 'bar',
      toolbar: {
        show: false,
      },
      events: {
        dataPointSelection: function(event, chartContext, config) {
          //console.log(config['dataPointIndex'])
          //handleChange(config['dataPointIndex'])
        }
      },
    },

    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: '55%',
      },
    },
    dataLabels: {
      enabled: false,
    },
    legend: {
      show: false,
    },
    colors: ['#5664d2', '#1cbb8c', '#ffff00'],
    yaxis: [{
      labels: {
        formatter: (value) => value.toFixed(0),
      },
      title: {
        text: 'Total',
      }
    }],
  }

  const series = [{
    name: 'Total',
    type: 'column',
    data: serieTotal,
  }, {
    name: 'Aprovados',
    type: 'column',
    data: serieAprovados,
  }, {
    name: 'Atrasados',
    type: 'column',
    data: serieAtrasados,
  }]

  return (
    <>
      <h4 className="card-title mb-4">
        Produtos de {monthToTextFromRef(props.mesRef)}
      </h4>
      <div>
        <div id="line-column-chart" className="apex-charts" dir="ltr">
          <ReactApexChart
            options={options}
            series={series}
            height="280"
          />
        </div>
      </div>
    </>
  )
}

export default DashboardProdutos;
